import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MapPin, DollarSign, Calendar, Clock, Globe, Briefcase, Award, Star, FileText, Heart, Edit, Trash2, ArrowLeft, Power, Check, X, Video, MessageSquare, Zap, Shield, Bot, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { getCertificationAcronym } from '../constants/certifications';
import { SERVICENOW_PRODUCTS, SECURITY_CLEARANCE_LEVELS } from '../utils/servicenowData';
import ScheduleInterviewModal from '../components/ScheduleInterviewModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format availability status
const formatAvailabilityStatus = (status) => {
  const statusMap = {
    'available': 'Available Now',
    'available_now': 'Available Now',
    'Available Now': 'Available Now',
    'available_soon': 'Available Soon',
    'Available Soon': 'Available Soon',
    'partially_available': 'Partially Available',
    'Partially Available': 'Partially Available',
    'not_available': 'Not Available',
    'Not Available': 'Not Available',
    'on_project': 'On Project',
    'On Project': 'On Project'
  };
  return statusMap[status] || status || 'Available';
};

// Helper to get language flag emoji
const getLanguageFlag = (language) => {
  const langName = typeof language === 'string' ? language : language?.language || '';
  const flagMap = {
    'English': '🇬🇧', 'French': '🇫🇷', 'German': '🇩🇪', 'Spanish': '🇪🇸',
    'Italian': '🇮🇹', 'Portuguese': '🇵🇹', 'Dutch': '🇳🇱', 'Polish': '🇵🇱',
    'Russian': '🇷🇺', 'Chinese': '🇨🇳', 'Japanese': '🇯🇵', 'Korean': '🇰🇷',
    'Arabic': '🇸🇦', 'Hindi': '🇮🇳', 'Swedish': '🇸🇪', 'Norwegian': '🇳🇴',
    'Danish': '🇩🇰', 'Finnish': '🇫🇮', 'Czech': '🇨🇿', 'Greek': '🇬🇷',
    'Turkish': '🇹🇷', 'Hebrew': '🇮🇱', 'Vietnamese': '🇻🇳', 'Thai': '🇹🇭',
    'Indonesian': '🇮🇩', 'Malay': '🇲🇾', 'Romanian': '🇷🇴', 'Hungarian': '🇭🇺',
    'Ukrainian': '🇺🇦', 'Bulgarian': '🇧🇬', 'Croatian': '🇭🇷', 'Slovak': '🇸🇰',
    'Slovenian': '🇸🇮', 'Lithuanian': '🇱🇹', 'Latvian': '🇱🇻', 'Estonian': '🇪🇪'
  };
  return flagMap[langName] || '🌐';
};

const ResourceProfilePage = () => {
  const { resourceId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [resource, setResource] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [ratings, setRatings] = useState(null);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const isRM = user?.role === 'resource_manager' || user?.role === 'operating_partner';
  const isPM = user?.role === 'project_manager';
  const isOwner = resource?.company_id === user?.company_id;

  useEffect(() => {
    fetchResourceDetails();
    checkIfFavorite();
    fetchRatings();
  }, [resourceId]);

  const fetchResourceDetails = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/resources/${resourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResource(response.data);
      
      if (response.data.company_id) {
        const companyResponse = await axios.get(`${API}/companies/${response.data.company_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCompany(companyResponse.data);
      }
    } catch (error) {
      console.error('Error fetching resource:', error);
      toast.error('Resource not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchRatings = async () => {
    try {
      const response = await axios.get(`${API}/ratings/resource/${resourceId}`);
      setRatings(response.data);
    } catch (error) {
      console.error('Error fetching ratings:', error);
    }
  };

  const checkIfFavorite = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/favorites/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorite(response.data.some(fav => fav.resource_id === resourceId));
    } catch (error) {
      console.error('Error checking favorite:', error);
    }
  };

  const toggleFavorite = async () => {
    const token = localStorage.getItem('token');
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/resources/${resourceId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites/resources/${resourceId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Added to favorites');
      }
      setIsFavorite(!isFavorite);
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const toggleActivation = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/resources/${resourceId}`, 
        { ...resource, is_activated: !resource.is_activated },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResource({ ...resource, is_activated: !resource.is_activated });
      toast.success(resource.is_activated ? 'Resource deactivated' : 'Resource activated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/resources/${resourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Resource deleted');
      navigate('/resources');
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="text-lg">Loading...</div></div>;
  }

  if (!resource) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card><CardContent className="p-6 text-center">Resource not found</CardContent></Card>
      </div>
    );
  }

  const fullName = resource.first_name && resource.last_name 
    ? `${resource.first_name} ${resource.last_name}` 
    : resource.name || 'Unknown';

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {isRM && isOwner && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/resources`, { state: { editResourceId: resourceId } })}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button variant="outline" onClick={toggleActivation}>
              <Power className="w-4 h-4 mr-2" />
              {resource.is_activated ? 'Deactivate' : 'Activate'}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Hero Section */}
      <Card>
        <CardContent className="p-8">
          <div className="flex items-start gap-6">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-3xl font-bold">
              {fullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
            </div>
            <div className="flex-1">
              <div className="flex items-center flex-wrap gap-3 mb-2">
                <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>{fullName}</h1>
                {resource.is_activated && (
                  <span className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 text-sm font-medium rounded-md border border-green-200">
                    <Check className="w-4 h-4" />
                    Active
                  </span>
                )}
                {resource.is_ai_augmented && (
                  <span className="flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 text-sm font-medium rounded-md border border-purple-200">
                    <Zap className="w-4 h-4" />
                    AI-Augmented
                  </span>
                )}
                {resource.security_clearance && resource.security_clearance !== 'none' && (
                  <span className="flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-700 text-sm font-medium rounded-md border border-amber-200" title={`${resource.clearance_country || ''} clearance`}>
                    <Shield className="w-4 h-4" />
                    {SECURITY_CLEARANCE_LEVELS.find(l => l.id === resource.security_clearance)?.name || resource.security_clearance}
                  </span>
                )}
              </div>
              {/* Role, Experience & Products - Key information at a glance */}
              <div className="mb-2">
                <p className="text-xl text-gray-700 font-medium">
                  {resource.roles && resource.roles.length > 0 
                    ? resource.roles.slice(0, 2).join(' • ')
                    : resource.role || 'ServiceNow Consultant'}
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-1">
                  <span className="text-sm text-gray-600">
                    {resource.servicenow_experience_years || resource.experience_years || 0} years on ServiceNow
                  </span>
                  {resource.servicenow_products && resource.servicenow_products.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {resource.servicenow_products.slice(0, 4).map((product, idx) => (
                        <span key={idx} className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">
                          {typeof product === 'string' ? product.toUpperCase() : product}
                        </span>
                      ))}
                      {resource.servicenow_products.length > 4 && (
                        <span className="text-xs text-gray-500">+{resource.servicenow_products.length - 4}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                <span className="px-2 py-1 bg-gray-100 rounded">{resource.resource_id}</span>
              </div>
              <div className="flex flex-wrap gap-4 text-sm">
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    `${resource.work_city || resource.city || ''}, ${resource.work_country || resource.country || ''}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-gray-700 hover:text-blue-600 hover:underline"
                >
                  <MapPin className="w-4 h-4 mr-1" />
                  {resource.work_city || resource.city}, {resource.work_country || resource.country}
                </a>
                {resource.can_work_remote && (
                  <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md border border-blue-200">Remote OK</span>
                )}
                {/* CV icon in header if CV available */}
                {(resource.cv_url || resource.cv_file_url) && (
                  <button 
                    onClick={() => window.open(resource.cv_url || resource.cv_file_url, '_blank')}
                    className="flex items-center text-blue-600 hover:text-blue-800 hover:underline px-2 py-1 bg-blue-50 rounded-md border border-blue-200"
                    title="View CV"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    CV
                  </button>
                )}
                {company && (
                  <button onClick={() => navigate(`/company/${company.id}`)} className="flex items-center text-blue-600 hover:underline">
                    <Briefcase className="w-4 h-4 mr-1" />
                    {company.name}
                  </button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content Grid: Left column (main) + Right column (sidebar) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-50 rounded-lg">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Daily Rate</p>
                    <p className="text-2xl font-bold">{resource.currency} {resource.daily_rate}</p>
                    {resource.long_term_discount > 0 && (
                      <p className="text-xs text-green-600">-{resource.long_term_discount}% long term</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Availability</p>
                    <p className="text-lg font-bold">{formatAvailabilityStatus(resource.availability_status)}</p>
                    {(resource.available_from || resource.availability_date) && (
                      <p className="text-xs text-gray-600">From {new Date(resource.available_from || resource.availability_date).toLocaleDateString()}</p>
                    )}
                    {(resource.commitment_capacity || resource.availability_capacity) && (
                      <p className="text-xs text-gray-600">Capacity: {resource.commitment_capacity || resource.availability_capacity}%</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <Star className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 mb-1">Experience</p>
                    {resource.servicenow_experience_years || resource.it_experience_years ? (
                      <div className="space-y-1">
                        {resource.servicenow_experience_years && (
                          <p className="text-lg font-bold text-purple-700">
                            {resource.servicenow_experience_years} years ServiceNow
                          </p>
                        )}
                        {resource.it_experience_years && (
                          <p className="text-sm text-gray-600">
                            {resource.it_experience_years} years IT experience
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-2xl font-bold">{resource.experience_years || 0} years</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Ratings Section */}
      {ratings && ratings.total_ratings > 0 && (
        <Card className="border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center text-yellow-800">
              <Star className="w-5 h-5 mr-2 fill-yellow-500 text-yellow-500" />
              Resource Rating
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6 mb-4">
              <div className="text-center">
                <span className="text-5xl font-bold text-yellow-700">
                  {ratings.average_score.toFixed(1)}
                </span>
                <span className="text-xl text-yellow-600">/5</span>
              </div>
              <div className="flex flex-col">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-6 h-6 ${
                        star <= Math.round(ratings.average_score)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm text-yellow-700 mt-1">
                  Based on {ratings.total_ratings} collaboration{ratings.total_ratings > 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {/* Reviews */}
            {ratings.ratings && ratings.ratings.length > 0 && (
              <div className="space-y-3 border-t border-yellow-200 pt-4">
                <p className="text-sm font-semibold text-yellow-800">Recent Feedback</p>
                <div className="grid md:grid-cols-2 gap-3">
                  {ratings.ratings.slice(0, 4).map((rating, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 border border-yellow-100 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= rating.score
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-gray-500 ml-auto">
                          {new Date(rating.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {rating.comment ? (
                        <p className="text-sm text-gray-700 italic">&ldquo;{rating.comment}&rdquo;</p>
                      ) : (
                        <p className="text-sm text-gray-400 italic">No comment provided</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Ratings Disclaimer */}
            <p className="text-xs text-gray-500 mt-4 pt-3 border-t border-yellow-200">
              Ratings reflect user feedback and do not constitute verification or endorsement by SnowExchange.
            </p>
          </CardContent>
        </Card>
      )}

          {/* About */}
      {resource.bio && (
        <Card>
          <CardHeader>
            <CardTitle>About</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-wrap">{resource.bio}</p>
          </CardContent>
        </Card>
      )}

          {/* Skills & Expertise */}
      <Card>
        <CardHeader>
          <CardTitle>Skills & Expertise</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Roles */}
            {resource.roles && resource.roles.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <Briefcase className="w-4 h-4 mr-2" />
                  ServiceNow Roles
                </h4>
                <div className="flex flex-wrap gap-2">
                  {resource.roles.map((role, idx) => (
                    <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-md border border-blue-200">
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Modules */}
            {resource.modules_mastered && resource.modules_mastered.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Modules Mastered</h4>
                <div className="flex flex-wrap gap-2">
                  {resource.modules_mastered.map((module, idx) => {
                    const getModuleAcronym = (m) => {
                      const map = {
                        'IT Service Management (ITSM)': 'ITSM',
                        'IT Operations Management (ITOM)': 'ITOM',
                        'IT Business Management (ITBM)': 'ITBM',
                        'Customer Service Management (CSM)': 'CSM',
                        'Field Service Management (FSM)': 'FSM',
                        'Human Resources Service Delivery (HRSD)': 'HRSD',
                        'Security Operations (SecOps)': 'SecOps',
                        'Governance, Risk and Compliance (GRC)': 'GRC'
                      };
                      return map[m] || m;
                    };
                    return (
                      <span key={idx} className="px-3 py-1 bg-indigo-50 text-indigo-700 text-sm rounded-md border border-indigo-200" title={module}>
                        {getModuleAcronym(module)}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Certifications */}
            {resource.certifications && resource.certifications.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <Award className="w-4 h-4 mr-2" />
                  Certifications
                </h4>
                <div className="flex flex-wrap gap-2">
                  {resource.certifications.map((cert, idx) => (
                    <span key={idx} className="px-3 py-1 text-sm rounded-md border" title={cert} style={{
                      background: 'rgba(98, 216, 78, 0.1)',
                      color: '#62D84E',
                      borderColor: 'rgba(98, 216, 78, 0.3)'
                    }}>
                      {getCertificationAcronym(cert)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Languages */}
            {resource.languages_spoken && resource.languages_spoken.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <Globe className="w-4 h-4 mr-2" />
                  Languages
                </h4>
                <div className="flex flex-wrap gap-2">
                  {resource.languages_spoken.map((lang, idx) => (
                    <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-md border border-gray-200 flex items-center gap-1">
                      <span>{getLanguageFlag(lang)}</span>
                      {typeof lang === 'string' ? lang : `${lang.language} • ${lang.level}`}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

          {/* ServiceNow Products & AI Enhancement Section */}
      {((resource.servicenow_products && resource.servicenow_products.length > 0) || 
        resource.is_ai_augmented || 
        (resource.security_clearance && resource.security_clearance !== 'none')) && (
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardHeader>
            <CardTitle className="flex items-center text-purple-800">
              <Sparkles className="w-5 h-5 mr-2" />
              ServiceNow Expertise & Capabilities
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* ServiceNow Products */}
              {resource.servicenow_products && resource.servicenow_products.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-purple-700 mb-3">ServiceNow Products</h4>
                  <div className="flex flex-wrap gap-2">
                    {resource.servicenow_products.map(productId => {
                      const product = SERVICENOW_PRODUCTS.find(p => p.id === productId);
                      return product && (
                        <span key={productId} className="px-3 py-1.5 bg-white text-purple-700 text-sm rounded-md border border-purple-200 shadow-sm" title={product.fullName}>
                          {product.name} <span className="text-purple-400 text-xs">({product.category})</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* AI-Augmented Details */}
              {resource.is_ai_augmented && (
                <div className="bg-white rounded-lg p-4 border border-purple-200">
                  <h4 className="text-sm font-semibold text-purple-700 mb-2 flex items-center">
                    <Zap className="w-4 h-4 mr-2" />
                    AI-Augmented Consultant
                  </h4>
                  <p className="text-sm text-gray-600 mb-3">
                    This consultant actively uses AI tools to enhance productivity and deliver higher quality work.
                  </p>
                  {resource.ai_agents && resource.ai_agents.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">AI Tools Used:</p>
                      <div className="flex flex-wrap gap-2">
                        {resource.ai_agents.map((agent, idx) => (
                          <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                            <Bot className="w-3 h-3" />
                            {agent}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Security Clearance Details */}
              {resource.security_clearance && resource.security_clearance !== 'none' && (
                <div className="bg-white rounded-lg p-4 border border-amber-200">
                  <h4 className="text-sm font-semibold text-amber-700 mb-2 flex items-center">
                    <Shield className="w-4 h-4 mr-2" />
                    Security Clearance
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 text-xs">Clearance Level</p>
                      <p className="font-medium text-gray-800">
                        {SECURITY_CLEARANCE_LEVELS.find(l => l.id === resource.security_clearance)?.name || resource.security_clearance}
                      </p>
                    </div>
                    {resource.clearance_country && (
                      <div>
                        <p className="text-gray-500 text-xs">Valid In</p>
                        <p className="font-medium text-gray-800">{resource.clearance_country}</p>
                      </div>
                    )}
                    {resource.nationality && (
                      <div>
                        <p className="text-gray-500 text-xs">Nationality</p>
                        <p className="font-medium text-gray-800">{resource.nationality}</p>
                      </div>
                    )}
                    {resource.country_of_residence && (
                      <div>
                        <p className="text-gray-500 text-xs">Country of Residence</p>
                        <p className="font-medium text-gray-800">{resource.country_of_residence}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

          {/* Work Preferences */}
      <Card>
        <CardHeader>
          <CardTitle>Work Preferences</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {resource.can_work_remote && (
              <div className="flex items-center text-sm">
                <Check className="w-4 h-4 mr-2 text-green-600" />
                Can work remotely
              </div>
            )}
            {resource.can_travel && (
              <div className="flex items-center text-sm">
                <Check className="w-4 h-4 mr-2 text-green-600" />
                Can travel {resource.max_travel_distance && `(max ${resource.max_travel_distance} km)`}
                {resource.max_onsite_days && ` • Max ${resource.max_onsite_days} days onsite`}
              </div>
            )}
            {resource.timezone_min && resource.timezone_max && (
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <Clock className="w-4 h-4 mr-2 text-gray-600" />
                  Timezone flexibility: {resource.timezone_min} → {resource.timezone_max}
                </div>
                {/* Visual timezone range */}
                <div className="ml-6 flex items-center space-x-1">
                  <span className="text-xs text-gray-500">{resource.timezone_min}</span>
                  <div className="flex-1 h-2 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-600 rounded-full mx-2" style={{ maxWidth: '200px' }}></div>
                  <span className="text-xs text-gray-500">{resource.timezone_max}</span>
                </div>
              </div>
            )}
            {resource.max_travel_distance && (
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <MapPin className="w-4 h-4 mr-2 text-gray-600" />
                  Travel radius: <span className="font-medium ml-1">{resource.max_travel_distance} km</span>
                  <span className="text-gray-500 ml-1">
                    from {resource.work_city || resource.city || 'location'}
                    {(resource.work_country || resource.country) && `, ${resource.work_country || resource.country}`}
                  </span>
                </div>
                {/* Google Maps link for location */}
                <div className="ml-6">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      `${resource.work_city || resource.city || ''}, ${resource.work_country || resource.country || ''}`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    <MapPin className="w-3 h-3 mr-1" />
                    View on Google Maps
                  </a>
                </div>
                {/* Visual travel radius indicator */}
                <div className="ml-6">
                  <div className="inline-flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    <div className="w-4 h-4 rounded-full bg-green-300"></div>
                    <div className="w-5 h-5 rounded-full bg-green-200"></div>
                    <div className="w-6 h-6 rounded-full bg-green-100 border-2 border-green-300"></div>
                    <span className="text-xs text-gray-500 ml-2">{resource.max_travel_distance} km radius</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

          {/* Documents */}
      <Card>
        <CardHeader>
          <CardTitle>Documents & Links</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {(resource.cv_url && resource.cv_url.trim() !== '') ? (
              <Button onClick={() => window.open(resource.cv_url, '_blank')} className="bg-blue-600 hover:bg-blue-700">
                <FileText className="w-4 h-4 mr-2" />
                View CV
              </Button>
            ) : (resource.cv_file_url && resource.cv_file_url.trim() !== '') ? (
              <Button onClick={() => window.open(resource.cv_file_url, '_blank')} className="bg-blue-600 hover:bg-blue-700">
                <FileText className="w-4 h-4 mr-2" />
                View CV
              </Button>
            ) : (
              <Button variant="outline" disabled>
                <FileText className="w-4 h-4 mr-2" />
                No CV uploaded
              </Button>
            )}
            {resource.linkedin && (
              <Button variant="outline" onClick={() => window.open(resource.linkedin, '_blank')}>
                LinkedIn Profile
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
        </div>

        {/* Sidebar - 1/3 width */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Add to Favorites */}
              {!isOwner && (
                <Button 
                  className="w-full"
                  variant={isFavorite ? 'default' : 'outline'}
                  onClick={toggleFavorite}
                >
                  <Heart className={`w-4 h-4 mr-2 ${isFavorite ? 'fill-current' : ''}`} />
                  {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                </Button>
              )}
              
              {/* Pre-Reserve Resource */}
              {!isOwner && (
                <Button 
                  className="w-full btn-snow"
                  onClick={() => {
                    navigate('/reservations', {
                      state: {
                        resourceId: resource.id,
                        resourceName: `${resource.first_name} ${resource.last_name}`,
                        action: 'pre-reserve'
                      }
                    });
                  }}
                >
                  Pre-Reserve Resource
                </Button>
              )}
              
              {/* Send Message */}
              {!isOwner && (
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => {
                    navigate('/messages', {
                      state: {
                        targetCompanyId: resource.company_id,
                        resourceInfo: {
                          id: resource.id,
                          name: `${resource.first_name} ${resource.last_name}`,
                          role: resource.role || resource.roles?.[0]
                        }
                      }
                    });
                  }}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              )}
              
              {/* Schedule Interview - only for PM */}
              {isPM && !isOwner && (
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => setShowInterviewModal(true)}
                >
                  <Video className="w-4 h-4 mr-2" />
                  Schedule Interview
                </Button>
              )}
              
              {/* Download Profile */}
              <Button 
                className="w-full" 
                variant="outline"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const response = await axios.get(`${API}/resources/${resourceId}/download-profile`, {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: 'blob'
                    });
                    
                    // Create download link
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `${resource.first_name}_${resource.last_name}_Profile.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    
                    toast.success('Profile downloaded successfully');
                  } catch (error) {
                    console.error('Failed to download profile:', error);
                    toast.error('Failed to download profile');
                  }
                }}
              >
                <FileText className="w-4 h-4 mr-2" />
                Download Profile
              </Button>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-sm">
                <p className="text-gray-500">Location</p>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    `${resource.work_city || resource.city || ''}, ${resource.work_country || resource.country || ''}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                >
                  <MapPin className="w-4 h-4" />
                  {resource.work_city || resource.city}, {resource.work_country || resource.country}
                </a>
              </div>
              {resource.timezone && (
                <div className="text-sm">
                  <p className="text-gray-500">Timezone</p>
                  <p className="font-medium">{resource.timezone}</p>
                </div>
              )}
              {company && (
                <div className="text-sm">
                  <p className="text-gray-500">Company</p>
                  <button 
                    onClick={() => navigate(`/company/${company.id}`)} 
                    className="font-medium text-blue-600 hover:underline"
                  >
                    {company.name}
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Availability Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Availability</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-sm">
                <p className="text-gray-500">Status</p>
                <p className="font-medium">{formatAvailabilityStatus(resource.availability_status)}</p>
              </div>
              {(resource.available_from || resource.availability_date) && (
                <div className="text-sm">
                  <p className="text-gray-500">Available From</p>
                  <p className="font-medium">{new Date(resource.available_from || resource.availability_date).toLocaleDateString()}</p>
                </div>
              )}
              {(resource.commitment_capacity || resource.availability_capacity) && (
                <div className="text-sm">
                  <p className="text-gray-500">Capacity</p>
                  <p className="font-medium">{resource.commitment_capacity || resource.availability_capacity}%</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Schedule Interview Modal */}
      {isPM && (
        <ScheduleInterviewModal
          isOpen={showInterviewModal}
          onClose={() => setShowInterviewModal(false)}
          resource={resource}
          company={company}
          onSuccess={() => {
            toast.success('Interview request sent!');
            // Optionally navigate to interviews page
          }}
        />
      )}
    </div>
  );
};

export default ResourceProfilePage;